import { useState } from 'react';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

interface LoginForm {
  email: string;
  password: string;
}

export function LoginPage(): React.ReactElement {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (values: LoginForm): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      await login(values.email, values.password);
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : '登录失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // 品牌浅紫渐变 + 两个柔光圆，弱化纯色背景的空旷感
        background:
          'radial-gradient(circle at 12% 18%, rgba(122,44,199,0.10), transparent 42%),' +
          'radial-gradient(circle at 88% 82%, rgba(122,44,199,0.08), transparent 45%),' +
          'linear-gradient(160deg, #F9F8FC 0%, #F1ECF8 100%)',
        padding: 24,
      }}
    >
      <Card
        style={{
          width: 400,
          borderRadius: 12,
          boxShadow: '0 12px 40px rgba(59,18,106,0.10)',
        }}
        styles={{ body: { padding: '36px 36px 32px' } }}
      >
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <img
            src="/assets/brand/logo-horizontal.png"
            alt="鲤鱼来了"
            style={{ height: 64, objectFit: 'contain' }}
          />
        </div>
        <Typography.Title
          level={4}
          style={{ textAlign: 'center', marginBottom: 4, marginTop: 12 }}
        >
          运营管理后台
        </Typography.Title>
        <Typography.Paragraph
          type="secondary"
          style={{ textAlign: 'center', fontSize: 13, marginBottom: 24 }}
        >
          后台账号与小程序用户相互独立，请使用后台账号登录
        </Typography.Paragraph>

        {error ? <Alert type="error" title={error} style={{ marginBottom: 16 }} showIcon /> : null}

        <Form<LoginForm> layout="vertical" onFinish={onSubmit} disabled={submitting}>
          <Form.Item
            label="邮箱"
            name="email"
            // 宽松校验：种子账号使用 liyu.local 等 .local 域，antd type:'email' 会误拒
            rules={[
              { required: true, message: '请输入正确的邮箱' },
              { pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: '请输入正确的邮箱' },
            ]}
          >
            <Input autoComplete="username" placeholder="请输入邮箱" />
          </Form.Item>
          <Form.Item
            label="密码"
            name="password"
            rules={[{ required: true, min: 8, message: '密码至少 8 位' }]}
          >
            <Input.Password autoComplete="current-password" placeholder="请输入密码" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={submitting} size="large">
            登 录
          </Button>
        </Form>
      </Card>

      <Typography.Text
        type="secondary"
        style={{ marginTop: 24, fontSize: 12, letterSpacing: 1 }}
      >
        鲤鱼来了 · Beyond Lydia
      </Typography.Text>
    </div>
  );
}
